import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API, toast } from '@/App';

const RESEND_SUCCESS_MESSAGE = 'Un nouvel email de vérification vous a été envoyé.';
const RESEND_ERROR_MESSAGE = "Impossible d’envoyer l’email pour le moment, réessayez plus tard.";

const EmailVerificationBanner = ({ user, onUserUpdate }) => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified !== false) {
    return null;
  }

  const handleResend = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification`);
      const payload = response.data?.data || {};
      if (payload.email_verified) {
        onUserUpdate?.({
          ...user,
          email_verified: true,
          email_verified_at: payload.email_verified_at || user.email_verified_at,
        });
        window.dispatchEvent(new Event('cnk-auth-refresh'));
      }
      toast.success(RESEND_SUCCESS_MESSAGE);
    } catch (_error) {
      toast.error(RESEND_ERROR_MESSAGE);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50" data-testid="email-verification-banner">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 text-amber-950">
          <div className="rounded-full bg-amber-100 p-2">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div>
            <p className="font-semibold">Veuillez verifier votre email</p>
            <p className="text-sm text-amber-900">
              Confirmez <span className="font-medium">{user.email}</span> pour debloquer les actions sensibles.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-amber-600 text-white hover:bg-amber-700"
            onClick={handleResend}
            disabled={sending}
            data-testid="email-verification-banner-resend"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${sending ? 'animate-spin' : ''}`} />
            {sending ? 'Envoi...' : 'Renvoyer email'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-amber-300 bg-white text-amber-950 hover:bg-amber-100"
            onClick={() => navigate('/verify-email', { state: { email: user.email, role: user.role } })}
            data-testid="email-verification-banner-open"
          >
            Ouvrir la page
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
